import { SplitNumberInput } from '@components'

import { Color, Divider, Flex, Space, Spacing, Text } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { MAX_INVOICE_DISCOUNT_PERCENT } from '@features/invoices/constants/maxInvoiceDiscountPercent'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { clampInvoiceDiscountValue } from '@features/invoices/utils/clampInvoiceDiscountValue'
import { getDiscountTypeTrackingValue } from '@features/invoices/utils/getDiscountTypeTrackingValue'
import { getInvoiceDiscountTypeSelectItems } from '@features/invoices/utils/getInvoiceDiscountTypeSelectItems'
import { isAmountDiscountType } from '@features/invoices/utils/isAmountDiscountType'
import { isPercentageDiscountType } from '@features/invoices/utils/isPercentageDiscountType'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'

import { useInvoiceEditorForm } from '../../../../../../../../contexts/invoiceEditorFormContext'

interface InvoiceGlobalDiscountProps {
  hasDiscountError: boolean
}

export const InvoiceGlobalDiscount = ({ hasDiscountError }: InvoiceGlobalDiscountProps) => {
  const { t } = useTranslation()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { getInvoiceLocale } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)

  const { FormItem, setValue, watch } = invoiceEditorForm
  const locale = getInvoiceLocale()

  const [currency, discount, discountType] = watch(['currency', 'discount', 'discountType'])

  const discountTypeSelectItems = useMemo(() => getInvoiceDiscountTypeSelectItems(currency), [currency])

  const isPercentageDiscount = isPercentageDiscountType(discountType)
  const isAmountDiscount = isAmountDiscountType(discountType)

  return (
    <Flex flexDirection="column" gap={Spacing.S4}>
      <Flex alignItems="center" gap={Spacing.S24}>
        <Text variant="bodySM" weight="semiBold">
          {t('external_invoices.editor.summary.discount')}
        </Text>
        <FormItem
          name="discount"
          render={({ field: { onChange, ...props } }) => (
            <SplitNumberInput
              key={discountType}
              {...props}
              aria-label={t('external_invoices.editor.summary.discount')}
              alignment="left"
              dropdownOptions={discountTypeSelectItems}
              error={hasDiscountError}
              locale={locale}
              max={isPercentageDiscount ? MAX_INVOICE_DISCOUNT_PERCENT : undefined}
              min={0}
              onChange={(discountValue) => {
                if (discountValue === undefined) {
                  return
                }
                const clampedDiscountValue = clampInvoiceDiscountValue(discountValue, discountType)
                onChange(clampedDiscountValue)
              }}
              onOptionChange={(type) => {
                setValue('discountType', type)
                const clampedDiscountValue = clampInvoiceDiscountValue(discount, type)
                onChange(clampedDiscountValue)
                trackInvoiceEditor('Form - Discount type changed', {
                  discountType: getDiscountTypeTrackingValue(type),
                })
              }}
              selectedOption={discountType}
              size="sm"
              withFormatting={isAmountDiscount}
            />
          )}
        />
      </Flex>
      {hasDiscountError && (
        <Text variant="bodySM" colorVariant="danger">
          {t('external_invoices.editor.summary.discount.error')}
        </Text>
      )}
      <Space size="s6" />
      <Divider dividerColor={Color.Grey950} />
      <Space size="s8" />
    </Flex>
  )
}
